import config from '../../../config';
import {
  AIClass,
  CubeDimensionEnum,
  FeatureEnum,
  ModelNamesBesidesFeatureEnums,
} from '../../../services/data/impl/cube/cubeSlice';
import { PointDetection } from '../../../services/data/impl/wrangler/schemas/pointDetections';
import { ScanData } from '../../scanDataApi/scanDataDefinitions';
import { ImageScaleParams } from '../ImageController';
import { CameraSelection, ImageIncrementDirection } from '../imageSlice';

const CLOUDFRONT_BASE_URL = config.api.imageUrl;
const ARN_PREFIX_REGEX = RegExp('s3://');
const DEPTH_MAP_REPLACE_REGEX = RegExp('(left|right)/[^/]*');

export interface imageQueryParams {
  format: string;
  width?: number;
}

/**
 * Builds a depth map url for a point
 * @param arn - an ARN for any of the images for the point
 * @param params - Any extra query parameters to add to the URL.
 * @returns depth map URL for the specified point
 */
export function getDepthMapUrl(
  arn: string | undefined,
  params: imageQueryParams
) {
  if (arn) {
    const depthMapUrl = new URL(
      arn
        .replace(ARN_PREFIX_REGEX, CLOUDFRONT_BASE_URL)
        .replace(DEPTH_MAP_REPLACE_REGEX, 'depth_maps')
    );
    for (const [key, value] of Object.entries(params)) {
      depthMapUrl.searchParams.append(key, value);
    }

    return depthMapUrl.toString();
  }
  return '';
}

/**
 * Builds the requested image url
 * @param point - The point to get an image for
 * @param params - Any extra query parameters to add to the URL.
 * @param cameraSelection - Choose between 'left' or 'right' camera. Defaults to 'left'.
 * @param colorCorrection - Whether the image is color corrected (true) or raw (false). Default is true.
 * @returns image url for the specified point, or null if no ARNs were provided
 */
export function getImageUrl(
  point: ScanData,
  cameraSelection: CameraSelection = 'left',
  colorCorrection = true,
  params: imageQueryParams
) {
  const arn = colorCorrection
    ? cameraSelection === 'left'
      ? point.point_img_left_cc
      : point.point_img_right_cc
    : cameraSelection === 'left'
    ? point.point_img_left_orig
    : point.point_img_right_orig;

  if (arn) {
    const imageUrl = new URL(
      arn.replace(ARN_PREFIX_REGEX, CLOUDFRONT_BASE_URL)
    );

    for (const [key, value] of Object.entries(params)) {
      imageUrl.searchParams.append(key, value);
    }

    return imageUrl.toString();
  }

  return '';
}

/**
 * Calculates center point within the parent element for image to be centered
 * @param containerWidth Width of the parent element
 * @param containerHeight Height of the parent element
 * @param imgWidth Width of the image
 * @param imgHeight Height of the image
 * @returns An object with `x` and `y` values that represent the center point
 */
export function calculateCenterPoint(
  containerWidth: number,
  containerHeight: number,
  imgWidth: number,
  imgHeight: number
): { x: number; y: number } {
  // Calculate center point for x axis based on given dimensions
  const x = containerWidth / 2 - imgWidth / 2;

  // Calculate center point for y axis based on given dimensions
  const y = containerHeight / 2 - imgHeight / 2;

  // Return an object containing the x and y coordinate values
  return { x, y };
}

/**
 * Finds and returns the next or previous CubePointGPSAndImages item in the neighbors array based on the given direction.
 *
 * @param currPointId - The current point ID to search for in the neighbors array.
 * @param neighbors - An array of ScanData point neighbors.
 * @param direction - A string representing the direction to search for the next item, either 'forward' or 'back'.
 * @param isAutoPlay - An optional boolean indicating if auto play mode is enabled. Default is false.
 * @param autoPlayIncrement - An optional integer representing the auto play increment. Default is 1.
 * @returns The next or previous ScanData item in the neighbors array based on the given direction, or null if not found or at the end of the array.
 */
export function findNextOrPreviousPoint(
  currPointId: string,
  neighbors: ScanData[],
  direction: ImageIncrementDirection,
  isAutoPlay = false,
  autoPlayIncrement = 1
): ScanData | null {
  if (!neighbors || neighbors.length === 0) {
    console.log('No neighbors available');
    return null;
  }

  const index = neighbors.findIndex((item) => item.point_id === currPointId);

  if (index === -1) {
    console.log('Current point not found in neighbors');
    return null;
  }

  const increment = isAutoPlay ? autoPlayIncrement : 1;

  if (direction === 'forward') {
    if (index + increment < neighbors.length) {
      // console.log('Found next point:', neighbors[index + increment]);
      return neighbors[index + increment];
    } else {
      console.log('Reached the end of the neighbors array');
      return null;
    }
  } else if (direction === 'back') {
    if (index - increment >= 0) {
      // console.log('Found previous point:', neighbors[index - increment]);
      return neighbors[index - increment];
    } else {
      console.log('Reached the beginning of the neighbors array');
      return null;
    }
  }

  console.log('Invalid direction provided');
  return null;
}

export interface EndOfList {
  forward: boolean;
  back: boolean;
  forwardAutoPlay: boolean;
  backAutoPlay: boolean;
}

/**
 * Detects if the current point is at the beginning or end of the neighbors array.
 *
 * @param currPointId - The current point ID to search for in the neighbors array.
 * @param neighbors - An array of ScanData point neighbors.
 * @param autoPlayIncrement - An integer representing the auto play increment.
 * @returns An EndOfList object with four boolean properties:
 *          forward - true if the current point is at the end of the list, false otherwise.
 *          back - true if the current point is at the beginning of the list, false otherwise.
 *          forwardAutoPlay - true if the current point is at the end of the list when considering the autoPlayIncrement, false otherwise.
 *          backAutoPlay - true if the current point is at the beginning of the list when considering the autoPlayIncrement, false otherwise.
 */

export function detectEndOfList(
  currPointId: string,
  neighbors: ScanData[],
  autoPlayIncrement: number
): EndOfList {
  if (!neighbors || neighbors.length === 0) {
    return {
      forward: true,
      back: true,
      forwardAutoPlay: true,
      backAutoPlay: true,
    };
  }

  const index = neighbors.findIndex((item) => item.point_id === currPointId);

  if (index === -1) {
    return {
      forward: true,
      back: true,
      forwardAutoPlay: true,
      backAutoPlay: true,
    };
  }

  const forward = index + 1 >= neighbors.length;
  const back = index - 1 < 0;
  const forwardAutoPlay = index + autoPlayIncrement >= neighbors.length;
  const backAutoPlay = index - autoPlayIncrement < 0;

  return { forward, back, forwardAutoPlay, backAutoPlay };
}

/**
 * Remove duplicates from an array of AI classes based on class name
 *
 * @param aiClasses - Array of AIClass objects
 *
 * @returns A new array of AIClass objects without duplicates by class name
 */
export function removeDuplicates(aiClasses: AIClass[]): AIClass[] {
  // Initialize a set to store unique class names
  const uniqueClassNames = new Set<FeatureEnum>();

  // Filter the array to include only the first occurrence of each class name
  const uniqueClasses = aiClasses.filter((aiClass) => {
    const className = aiClass[CubeDimensionEnum.className];

    // If we've seen this class name before, filter out this object
    if (uniqueClassNames.has(className)) {
      return false;
    }

    // If we haven't seen this class name before, add it to the set and keep this object
    uniqueClassNames.add(className);
    return true;
  });

  return uniqueClasses;
}

/**
 * Filters a PointDetection array by keeping only the Detection with the highest model_version
 * for each className. This function handles versions containing both words and numbers,
 * preferring versions with both, and otherwise the highest number. It also addresses edge cases
 * such as different model names with the same version and className, and specific handling for
 * 'blueberry' className to resolve issues with multiple model versions.
 *
 * @param pointDetections - A PointDetection array to filter. If undefined, returns undefined.
 * @param currAIServiceVersionOverride - The current AI service version override to use for filtering from the QA dropdown.
 * @returns A new PointDetection array with filtered items, or undefined if input was undefined.
 */
export function filterDetectionsByModelVersion(
  pointDetections: PointDetection[] | undefined,
  currAIServiceVersionOverride?: string
): PointDetection[] | undefined {
  if (!pointDetections) {
    return undefined;
  }
  // Map to store the highest version Detection for each className
  const classNameToDetection = new Map<string, PointDetection>();

  let pointDetectionsFiltered;

  // If the current AI service version override is set, filter and show only that version
  if (currAIServiceVersionOverride) {
    pointDetectionsFiltered = pointDetections.filter(
      (detection) => detection.model_version === currAIServiceVersionOverride
    );
  } else {
    pointDetectionsFiltered = pointDetections;
  }

  // Process each Detection to determine if it should be kept
  pointDetectionsFiltered.forEach((detection) => {
    const existingDetection = classNameToDetection.get(detection.class_name);
    const isGrapeEarlyMidSeason =
      detection.model_name ===
      ModelNamesBesidesFeatureEnums.grape_early_mid_season;
    const isGrapeEarlyMidSeasonInExistingDetection =
      existingDetection?.model_name ===
      ModelNamesBesidesFeatureEnums.grape_early_mid_season;

    // Replace existing detection if the current one has a better version
    // FIXME: if model is early mid season and already set - keep it
    if (
      !isGrapeEarlyMidSeason &&
      !isGrapeEarlyMidSeasonInExistingDetection &&
      (!existingDetection ||
        compareModelVersions(
          detection.model_version,
          existingDetection.model_version
        ) > 0)
    ) {
      classNameToDetection.set(detection.class_name, detection);
    } else if (
      // FIXME: if model is early mid season but didn't set - set it
      isGrapeEarlyMidSeason &&
      !isGrapeEarlyMidSeasonInExistingDetection
    ) {
      classNameToDetection.set(detection.class_name, detection);
    } else if (
      // FIXME: specific model versioning for early mid season case
      isGrapeEarlyMidSeason &&
      isGrapeEarlyMidSeasonInExistingDetection &&
      compareModelVersions(
        detection.model_version,
        existingDetection.model_version
      ) > 0
    ) {
      classNameToDetection.set(detection.class_name, detection);
    } else if (
      existingDetection &&
      existingDetection.model_name !== detection.model_name &&
      existingDetection.model_version === detection.model_version
    ) {
      // Handle edge case: different model names with the same version and className (models inflorescence vs inflorescence_juice which have the same version and className).  Assume the one with the longer model name is the newer one and keep it (workaround for now).
      if (existingDetection.model_name.length <= detection.model_name.length) {
        classNameToDetection.set(detection.class_name, detection);
      } else {
        console.log(
          `Found two detections with the same className and model_version but different model names. Taking the first one in the list. Detection 1: Model = ${existingDetection.model_name} Class = ${existingDetection.class_name} Version = ${existingDetection.model_version}, Detection 2: Model = ${detection.model_name} Class = ${detection.class_name} Version = ${detection.model_version}`
        );
      }
    }
  });

  // Determine the highest version number for 'blueberry' className
  let highestBlueberryVersion = '0';
  classNameToDetection.forEach((detection) => {
    if (detection.class_name.includes('blueberry')) {
      if (
        compareModelVersions(detection.model_version, highestBlueberryVersion) >
        0
      ) {
        highestBlueberryVersion = detection.model_version;
      }
    }
  });

  // Remove 'blueberry' detections not matching the highest version number.  This handles the case where multiple blueberry models were run on the same point but not every color was run on every point, so it's possible for a point to have multiple blueberry masks with different model versions.  The backend team is fixing this so every point will have every color eventually which will be "0" detections instead of just missing, but for now we need to handle this edge case.
  classNameToDetection.forEach((detection, class_name) => {
    if (
      detection.class_name.includes('blueberry') &&
      detection.model_version !== highestBlueberryVersion
    ) {
      classNameToDetection.delete(class_name);
    }
  });

  // Create an array of the filtered detections
  const filteredItems = Array.from(classNameToDetection.values());

  return filteredItems;
}

/**
 * Compares two model versions, which may be numbers, or strings containing a word followed by a number.
 * If both are numbers, returns a positive number if versionA is higher, a negative number if versionB is higher, and 0 if equal.
 * If both are word-number strings, returns a positive number if versionA's number is higher, a negative number if versionB's number is higher, and 0 if equal.
 * If versionA is a word-number string and versionB is a number, returns a positive number.
 * If versionB is a word-number string and versionA is a number, returns a negative number.
 *
 * @param versionA - First model version to compare
 * @param versionB - Second model version to compare
 *
 * @returns Positive number if versionA is considered higher, negative number if versionB is considered higher, and 0 if considered equal.
 */
export function compareModelVersions(
  versionA: string,
  versionB: string
): number {
  const wordNumberMatchA =
    versionA.match(/(\D+)(\d+)/) || versionA.match(/(\d+)(\D+)/);
  const wordNumberMatchB =
    versionB.match(/(\D+)(\d+)/) || versionB.match(/(\d+)(\D+)/);

  if (wordNumberMatchA && wordNumberMatchB) {
    // extract and compare the numbers
    const result =
      Number(versionA.match(/\d+/)) - Number(versionB.match(/\d+/));
    // if numbers are equal, return longer version name to keep consistent with current workaround
    return result || versionA.length - versionB.length;
  } else if (wordNumberMatchA) {
    return 1;
  } else if (wordNumberMatchB) {
    return -1;
  } else {
    return Number(versionA) - Number(versionB);
  }
}

/**
 * Returns an array of mask points scaled and positioned according to the image scaling parameters and dimensions.
 *
 * @param mask - The mask points (normalized from 0 to 1) to be transformed.
 * @param imageScaleParams - The image scaling parameters.
 * @param imageWidth - The width of the image.
 * @param imageHeight - The height of the image.
 * @returns An array of transformed mask points.
 */
export const scaledPoints = (
  mask: number[][],
  imageScaleParams: ImageScaleParams,
  imageWidth: number,
  imageHeight: number
): number[] =>
  mask
    .flat()
    .map((coord, index) =>
      index % 2 === 0
        ? coord * imageScaleParams.scale * imageWidth + imageScaleParams.x
        : coord * imageScaleParams.scale * imageHeight + imageScaleParams.y
    );

/**
 * Normalizes a grayscale image and applies a colormap to it.
 *
 * @param depthMapImage The grayscale depth map image as an HTMLImageElement.
 * @returns The colorized depth map as an HTMLImageElement.
 */
export const colorizeDepthMap = (
  depthMapImage: HTMLImageElement
): HTMLImageElement | null => {
  // Initialize canvas and drawing context
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null; // Handle the null case
  }

  // Draw the depth map image on the canvas and get its pixel data
  canvas.width = depthMapImage.width;
  canvas.height = depthMapImage.height;
  ctx.drawImage(depthMapImage, 0, 0);
  const depthMap = ctx.getImageData(0, 0, canvas.width, canvas.height);

  // Create a new ImageData object for the colorized output
  const newImageData = new ImageData(depthMap.width, depthMap.height);

  // Define the upper limit and invalid value
  const DEPTH_MAX = 254; // 2.5 in python version but we are scaled from 0-255 on this version so need to scale
  const INVALID_DEPTH = -1;

  // Initialize variables for finding min and max depth
  let depth_min = DEPTH_MAX;
  let depth_max = INVALID_DEPTH;

  // Find the minimum and maximum valid depth values
  for (let i = 0; i < depthMap.data.length; i += 4) {
    const val = depthMap.data[i];
    if (val <= DEPTH_MAX && val !== INVALID_DEPTH) {
      depth_min = Math.min(depth_min, val);
      depth_max = Math.max(depth_max, val);
    }
  }

  // Calculate the range for normalization
  const range = depth_max - depth_min;

  // Map the grayscale values to the colorMap
  for (let i = 0; i < depthMap.data.length; i += 4) {
    const val = depthMap.data[i];

    // Handle invalid and out-of-range values
    if (val === INVALID_DEPTH || val > DEPTH_MAX) {
      newImageData.data.set([0, 0, 0, 255], i); // Black in RGBA
      continue;
    }

    // Normalize and map to color
    const normalizedVal = (val - depth_min) / range;
    const mappedIndex = Math.floor(normalizedVal * (colorMap.length - 1));

    const [r, g, b] = colorMap[mappedIndex];

    // Set the RGB and alpha values in the new ImageData
    newImageData.data.set([r, g, b, 255], i); // RGBA
  }

  // Draw the new colorized ImageData onto the canvas
  ctx.putImageData(newImageData, 0, 0);

  // Create a new HTMLImageElement from the colorized canvas
  const imgElement = new Image();
  imgElement.src = canvas.toDataURL();

  return imgElement;
};

// Color map extracted from openCV's COLORMAP_JET image so it matches what the AI team is used to seeing.  I used chatgpt code interpreter and uploaded the image to extract the color map. https://docs.opencv.org/4.x/d3/d50/group__imgproc__colormap.html#ga9a805d8262bcbe273f16be9ea2055a65
const colorMap: number[][] = [
  [0, 0, 132],
  [0, 0, 142],
  [0, 0, 147],
  [0, 0, 152],
  [0, 0, 158],
  [0, 0, 170],
  [0, 0, 173],
  [0, 0, 179],
  [0, 0, 184],
  [0, 0, 186],
  [0, 0, 192],
  [0, 0, 212],
  [0, 0, 222],
  [0, 0, 228],
  [0, 0, 234],
  [0, 0, 238],
  [0, 0, 244],
  [0, 0, 248],
  [0, 1, 127],
  [0, 1, 128],
  [0, 1, 164],
  [0, 1, 190],
  [0, 1, 207],
  [0, 2, 253],
  [0, 5, 255],
  [0, 8, 255],
  [0, 17, 255],
  [0, 21, 255],
  [0, 26, 255],
  [0, 29, 255],
  [0, 33, 255],
  [0, 41, 255],
  [0, 44, 254],
  [0, 53, 253],
  [0, 56, 253],
  [0, 65, 253],
  [0, 73, 255],
  [0, 77, 255],
  [0, 82, 255],
  [0, 86, 254],
  [0, 89, 255],
  [0, 93, 255],
  [0, 97, 254],
  [0, 109, 254],
  [0, 113, 254],
  [0, 118, 254],
  [0, 121, 254],
  [0, 123, 255],
  [0, 128, 255],
  [0, 132, 255],
  [0, 140, 255],
  [0, 144, 255],
  [0, 148, 254],
  [0, 152, 254],
  [0, 156, 255],
  [0, 160, 255],
  [0, 163, 254],
  [0, 168, 255],
  [0, 172, 255],
  [0, 175, 254],
  [0, 180, 255],
  [0, 193, 254],
  [0, 196, 255],
  [0, 199, 255],
  [0, 204, 255],
  [0, 208, 255],
  [0, 213, 255],
  [0, 217, 255],
  [0, 220, 255],
  [0, 228, 255],
  [0, 232, 255],
  [0, 237, 255],
  [0, 240, 255],
  [0, 244, 254],
  [1, 0, 138],
  [1, 0, 154],
  [1, 0, 202],
  [1, 0, 216],
  [1, 0, 218],
  [1, 0, 224],
  [1, 0, 251],
  [1, 1, 161],
  [1, 1, 197],
  [1, 13, 255],
  [1, 37, 255],
  [1, 49, 255],
  [1, 69, 255],
  [1, 100, 255],
  [1, 105, 255],
  [1, 135, 255],
  [1, 183, 255],
  [1, 226, 255],
  [1, 248, 255],
  [2, 60, 255],
  [2, 186, 255],
  [2, 251, 255],
  [4, 255, 250],
  [6, 255, 249],
  [10, 255, 247],
  [13, 255, 241],
  [18, 255, 237],
  [23, 255, 233],
  [27, 255, 230],
  [30, 255, 225],
  [34, 255, 222],
  [37, 255, 218],
  [41, 255, 215],
  [45, 255, 210],
  [51, 255, 204],
  [56, 255, 200],
  [60, 255, 197],
  [61, 255, 195],
  [68, 255, 186],
  [70, 255, 185],
  [73, 255, 181],
  [77, 255, 177],
  [82, 255, 173],
  [87, 255, 168],
  [90, 254, 165],
  [94, 255, 161],
  [97, 255, 158],
  [101, 255, 154],
  [104, 255, 150],
  [109, 255, 144],
  [114, 255, 140],
  [120, 255, 135],
  [122, 254, 132],
  [125, 255, 129],
  [129, 0, 0],
  [130, 255, 123],
  [131, 255, 121],
  [132, 0, 0],
  [134, 0, 0],
  [135, 255, 117],
  [139, 1, 1],
  [141, 255, 115],
  [143, 1, 0],
  [146, 0, 1],
  [146, 255, 110],
  [150, 0, 1],
  [150, 254, 105],
  [154, 0, 0],
  [154, 255, 101],
  [158, 255, 98],
  [159, 1, 0],
  [161, 255, 96],
  [163, 1, 0],
  [164, 255, 90],
  [166, 1, 0],
  [168, 255, 88],
  [171, 255, 84],
  [172, 0, 0],
  [175, 255, 80],
  [177, 0, 0],
  [179, 254, 77],
  [181, 255, 75],
  [183, 0, 1],
  [185, 255, 73],
  [186, 0, 0],
  [188, 255, 70],
  [191, 255, 67],
  [193, 0, 0],
  [195, 255, 65],
  [196, 0, 0],
  [199, 255, 61],
  [200, 0, 0],
  [202, 254, 59],
  [204, 255, 57],
  [205, 0, 0],
  [208, 255, 54],
  [209, 0, 0],
  [211, 255, 52],
  [213, 0, 0],
  [215, 255, 49],
  [217, 0, 0],
  [218, 255, 47],
  [221, 255, 44],
  [223, 0, 0],
  [224, 255, 42],
  [226, 0, 0],
  [229, 255, 39],
  [232, 0, 0],
  [233, 255, 37],
  [235, 0, 0],
  [236, 255, 35],
  [238, 0, 0],
  [241, 255, 32],
  [243, 0, 0],
  [244, 255, 30],
  [246, 0, 0],
  [247, 255, 28],
  [249, 0, 0],
  [251, 255, 25],
  [253, 0, 0],
  [254, 255, 23],
  [255, 0, 0],
  [255, 3, 0],
  [255, 9, 0],
  [255, 12, 0],
  [255, 16, 0],
  [255, 20, 0],
  [255, 24, 0],
  [255, 27, 0],
  [255, 34, 0],
  [255, 36, 0],
  [255, 39, 0],
  [255, 44, 0],
  [255, 48, 0],
  [255, 54, 0],
  [255, 59, 0],
  [255, 63, 0],
  [255, 67, 0],
  [255, 71, 0],
  [255, 75, 0],
  [255, 79, 0],
  [255, 84, 0],
  [255, 88, 0],
  [255, 92, 0],
  [255, 96, 0],
  [255, 100, 0],
  [255, 105, 0],
  [255, 109, 0],
  [255, 113, 0],
  [255, 118, 0],
  [255, 122, 0],
  [255, 126, 0],
  [255, 131, 0],
  [255, 135, 0],
  [255, 140, 0],
  [255, 144, 0],
  [255, 148, 0],
  [255, 153, 0],
  [255, 157, 0],
  [255, 161, 0],
  [255, 165, 0],
  [255, 170, 0],
  [255, 174, 0],
  [255, 178, 0],
  [255, 182, 0],
  [255, 187, 0],
  [255, 191, 0],
  [255, 196, 0],
  [255, 200, 0],
  [255, 205, 0],
  [255, 209, 0],
  [255, 214, 0],
  [255, 218, 0],
  [255, 222, 0],
  [255, 227, 0],
  [255, 228, 0],
  [255, 232, 0],
  [255, 236, 0],
  [255, 240, 0],
  [255, 245, 0],
  [255, 251, 1],
];
